import React from 'react';

export interface TabItem<T extends string> {
  value: T;
  label: React.ReactNode;
  count?: number;
}

/**
 * Underline tabs for switching sections of a page (detail pages).
 * For small filters or view toggles use SegmentedControl instead.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className = '',
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={`-mx-4 flex gap-5 overflow-x-auto border-b border-neutral-200/70 px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 ${className}`}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={`relative inline-flex h-10 flex-shrink-0 items-center gap-1.5 whitespace-nowrap text-[13px] transition-colors focus-visible:outline-none ${
              active ? 'text-ink' : 'text-neutral-500 hover:text-ink'
            }`}
          >
            {item.label}
            {item.count !== undefined && (
              <span className="rounded-full bg-neutral-100 px-1.5 text-[11px] tabular-nums text-neutral-500">{item.count}</span>
            )}
            <span
              className={`absolute inset-x-0 -bottom-px h-0.5 rounded-full transition-colors ${active ? 'bg-primary-600' : 'bg-transparent'}`}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}
